// ============================================================
// historyShared - the multiplayer half of useHistory.
//
// Recording an action onto the shared stack (with its baseline and the
// debounced, retried push), restoring a shared snapshot, and the
// optimistic undo. Plain functions over a context the hook builds each
// render from its refs and props - so a timer scheduled in one render
// keeps the values of that render, exactly as the closures it replaced.
// ============================================================

import type { PhaseFrame } from '../engine/phase/types';
import type { MutableRefObject, Dispatch, SetStateAction } from 'react';
import {
  pushSharedHistoryBatch,
  moveSharedHistoryPointer,
  batchRestoreCombatantStates,
  deleteNarrativeAfter,
  clearSharedHistory,
} from '../multiplayer';
import type { SharedHistoryPushResult } from '../multiplayer';
import type { Combatant, CombatState } from '../engine/roster/types';
import type { ChargeLine, HistorySnapshot, TurnTrackerState } from '../types';
import {
  clockedRestore,
  clockedStates,
  restoreChangesAnything,
  sameContent,
  snapshotOf,
  cohesionDiffers,
} from './historySnapshots';

type Timer = ReturnType<typeof setTimeout>;

interface SharedHistoryCache {
  stack: HistorySnapshot[];
  pointer: number;
}

/**
 * How many times a history push is worth trying, and how long to wait.
 *
 * The transaction is a read-modify-write on ONE document, so two clients
 * acting at the same moment collide by design and Firestore answers
 * `failed-precondition`. It retries internally and usually wins; under
 * load it can run out. Backing off in tens of milliseconds keeps a retry
 * invisible - the Undo button appears a blink later rather than never.
 */
const HISTORY_PUSH_RETRIES = 4;
const HISTORY_PUSH_BACKOFF_MS = 120;
const HISTORY_PUSH_DEBOUNCE_MS = 200;

/**
 * How many extra renders to wait for a post-state that has not landed.
 *
 * The observed lag is exactly one render. Two is one more than that, and a
 * bound is what stops an action that truly changes nothing from re-arming for
 * ever - see `holdBaseline`.
 */
const MAX_HELD_RETRIES = 2;

/** The shared-history refs and props, as one render sees them. Every sync ref is null-safe for solo mode. */
export interface SharedHistoryContext {
  sessionId: string;
  playerId: string | null;
  roster: Combatant[];
  turnTrackerState?: TurnTrackerState;
  /** The GM's frame as it stands, to ride in the snapshot. */
  frame?: PhaseFrame | null;
  /** The Cohesion pools as they stand (Squad Mode), part of the snapshot's content. */
  cohesion?: unknown;
  historyTrack: MutableRefObject<boolean>;
  undoingRef: MutableRefObject<boolean>;
  preActionSnapshotRef: MutableRefObject<HistorySnapshot | null>;
  historyPushTimerRef: MutableRefObject<Timer | null>;
  historyPushAccRef: MutableRefObject<HistorySnapshot[]>;
  heldRetriesRef: MutableRefObject<number>;
  setSharedHistoryState?: Dispatch<SetStateAction<{ canUndo: boolean }>>;
  sharedHistoryCacheRef?: MutableRefObject<SharedHistoryCache>;
  undoRedoBusyRef?: MutableRefObject<boolean>;
  /**
   * An action's entry is queued or being written (2026-10-24): Undo waits, or it would take back the action BEFORE
   * the one just taken. `setSaving` raises and lowers it; `savingRef` is its value now, for a Ctrl+Z.
   */
  setSaving?: (saving: boolean) => void;
  savingRef?: MutableRefObject<boolean>;
  /** A push is on its way to the server (between the debounce and its settling). */
  pushingRef?: MutableRefObject<boolean>;
  /** The release that lowers `saving` when an action's snapshot is followed by no step (`beginAction`). */
  savingFallbackRef?: MutableRefObject<Timer | null>;
}

/** The push's destination and the two refs a failure puts entries back through. */
type PushTarget = Pick<SharedHistoryContext, 'sessionId' | 'playerId' | 'historyPushAccRef' | 'historyPushTimerRef'>;

/**
 * Push, and PUT IT BACK if it fails.
 *
 * The accumulator is emptied before the transaction - it has to be, or a
 * second action queued during the round trip would be written twice. But
 * the failure path then dropped those entries on the floor: nothing
 * written, nothing retried, and `canUndo` never true for an action that
 * definitely happened (v8.25.0). Anything requeued goes to the FRONT,
 * because history is a sequence and the entries that failed are older
 * than whatever has arrived since.
 */
function pushBatchWithRetry(
  target: PushTarget,
  snaps: HistorySnapshot[],
  attempt = 1,
): Promise<SharedHistoryPushResult | null> {
  const { sessionId, playerId, historyPushAccRef: accRef, historyPushTimerRef: timerRef } = target;
  return pushSharedHistoryBatch(sessionId, snaps, playerId).catch((err) => {
    if (attempt >= HISTORY_PUSH_RETRIES) {
      // Out of tries. Put them back anyway: the next action's push takes
      // them along, which is a later Undo rather than a lost one.
      accRef.current.unshift(...snaps);
      console.error('[history] push failed, entries requeued', err);
      return null;
    }
    return new Promise<SharedHistoryPushResult | null>((resolve) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        resolve(pushBatchWithRetry(target, snaps, attempt + 1));
      }, HISTORY_PUSH_BACKOFF_MS * attempt);
    });
  });
}

/** Adopt what the server's stack now is, and enable Undo from the pointer. */
function adoptPointer(ctx: SharedHistoryContext, cache: SharedHistoryCache | undefined, pointer: number): void {
  if (ctx.sharedHistoryCacheRef && cache) ctx.sharedHistoryCacheRef.current = cache;
  if (ctx.setSharedHistoryState) ctx.setSharedHistoryState({ canUndo: pointer > 0 });
}

/**
 * OUR OWN WRITE IS OUR OWN NEWS. Waiting for the snapshot listener to
 * report it back is a dependency on a channel that can go quiet -
 * measured at ~15% of two-client runs, where the transaction committed
 * and no snapshot ever arrived, leaving Undo disabled over a stack that
 * had two entries in it. The listener still serves the OTHER clients.
 * An undo/redo in flight owns the pointer, so leave it be - the same
 * rule the listener follows.
 */
function adoptPushResult(ctx: SharedHistoryContext, result: SharedHistoryPushResult | null): void {
  if (!result || ctx.undoRedoBusyRef?.current) return;
  adoptPointer(ctx, result, result.pointer);
}

/**
 * Queue the entries and (re)start the debounce; the whole accumulator goes in one transaction. Saving from the
 * queueing until a push settles with no later push waiting behind it. A push that finally fails lowers it too: its
 * entries are requeued for the next action's push, and an offline table must not lose Undo until then.
 */
function queuePush(ctx: SharedHistoryContext, entries: HistorySnapshot[]): void {
  const { historyPushAccRef: accRef, historyPushTimerRef: timerRef } = ctx;
  accRef.current.push(...entries);
  ctx.setSaving?.(true);
  if (timerRef.current) clearTimeout(timerRef.current);
  timerRef.current = setTimeout(() => {
    timerRef.current = null;
    pushQueued(ctx);
  }, HISTORY_PUSH_DEBOUNCE_MS);
}

/** Push the whole accumulator; once it settles, Undo comes back unless a later push waits behind it. */
function pushQueued(ctx: SharedHistoryContext): void {
  const pushing = ctx.pushingRef ?? { current: false };
  const allSnaps = ctx.historyPushAccRef.current.splice(0);
  pushing.current = true;
  pushBatchWithRetry(ctx, allSnaps).then((result) => {
    pushing.current = false;
    adoptPushResult(ctx, result);
    if (!ctx.historyPushTimerRef.current) ctx.setSaving?.(false);
  });
}

/** How often a waiting Undo looks again, and how long it waits at most (past the no-step release). */
const SAVED_POLL_MS = 50;
const SAVED_WAIT_MS = 5000;

/** Resolves once nothing of this screen's is being saved (or after SAVED_WAIT_MS, whatever stands then). */
async function untilSaved(ctx: SharedHistoryContext): Promise<void> {
  for (let waited = 0; ctx.savingRef?.current && waited < SAVED_WAIT_MS; waited += SAVED_POLL_MS) {
    await new Promise((resolve) => setTimeout(resolve, SAVED_POLL_MS));
  }
}

/** How long an action's snapshot may stand with no step queued before Undo comes back (a pool's round trip fits). */
const NO_STEP_MS = 3000;

/**
 * An action begins (its snapshot is taken): Undo waits from HERE, not from its step's queueing - a kill-team verb's
 * pool round trip, or one render, lies between (2026-10-24). An action that is refused, or changes nothing, queues no
 * step: after NO_STEP_MS, with nothing queued or on its way, Undo comes back.
 */
export function beginAction(ctx: SharedHistoryContext): void {
  ctx.setSaving?.(true);
  const fallback = ctx.savingFallbackRef;
  if (!fallback) return;
  if (fallback.current) clearTimeout(fallback.current);
  fallback.current = setTimeout(() => {
    fallback.current = null;
    const idle = !ctx.historyPushTimerRef.current && !ctx.historyPushAccRef.current.length && !ctx.pushingRef?.current;
    if (idle) ctx.setSaving?.(false);
  }, NO_STEP_MS);
}

/**
 * THE POST IS A RENDER LATE, NOT ABSENT - so hold the baseline and wait
 * for it (v8.149.0).
 *
 * The transaction drops any snapshot identical to the top of the stack,
 * so a post taken before its state has landed would collapse the pair to
 * ONE entry with `pointer` at 0 - Undo disabled over an action that did
 * happen, and the baseline consumed by a push that wrote nothing of it
 * (measured: `sent=2 baselines=1 stack=1 pointer=0`, v8.33.0). So the
 * baseline is HELD, not spent, until a post genuinely differs.
 *
 * Spending the FLAG at the same time was the second half of the bug:
 * `applyHits` arms it beside its `updateTab`, yet the armed render can
 * still carry the roster from before that write. Re-arming lets the next
 * render push the change. Firestore never enters this branch (a local
 * write lands in the same beat); a backend that echoes from the server
 * does. BOUNDED, because an action that genuinely changes nothing would
 * otherwise re-arm on every render for ever.
 */
function holdBaseline(ctx: SharedHistoryContext, preSnap: HistorySnapshot): void {
  ctx.preActionSnapshotRef.current = preSnap;
  if (ctx.heldRetriesRef.current < MAX_HELD_RETRIES) {
    ctx.heldRetriesRef.current += 1;
    ctx.historyTrack.current = true;
  }
}

/**
 * Record a tracked action onto the shared stack: its post-state, and the
 * pre-state as a BASELINE.
 *
 * Only the post is pushed as an entry - pre-snapshots frequently mismatch
 * the history top when several actions queue within the debounce window,
 * and the transaction's dedup handles the post against the actual top.
 * The baseline is ALWAYS SENT, NEVER DECIDED HERE (v8.2.2): an empty stack
 * has no previous post to serve as the first action's pre-state, and only
 * the transaction knows whether the server stack is empty - it drops a
 * baseline when it is not. Asking the listener-filled local cache instead
 * was the undo flake: it could say "not empty" while the server was.
 */
export function recordSharedAction(ctx: SharedHistoryContext): void {
  const postSnapshot = snapshotOf(ctx.roster, ctx.turnTrackerState, Date.now(), false, ctx.frame, ctx.cohesion);
  const preSnap = ctx.preActionSnapshotRef.current;
  ctx.preActionSnapshotRef.current = null;
  if (preSnap && sameContent(preSnap, postSnapshot)) {
    holdBaseline(ctx, preSnap);
    return;
  }
  ctx.heldRetriesRef.current = 0;
  // A charge's start and its move line ride the action's own entry: the baseline is dropped on a stack that is not
  // empty.
  if (preSnap?.undoTokens) postSnapshot.undoTokens = preSnap.undoTokens;
  if (preSnap?.chargeLine) postSnapshot.chargeLine = preSnap.chargeLine;
  queuePush(ctx, preSnap ? [{ ...preSnap, baseline: true }, postSnapshot] : [postSnapshot]);
}

/**
 * Forget the shared half: the pending push, the pre-action snapshot, the
 * cached stack and the server's. A scene cut's reset (v8.337.0) - or the
 * next debounce would push the old stage back.
 */
export function resetSharedHistory(
  ctx: Pick<SharedHistoryContext, 'historyPushAccRef' | 'preActionSnapshotRef' | 'historyPushTimerRef'> &
    Partial<Pick<SharedHistoryContext, 'sharedHistoryCacheRef' | 'setSharedHistoryState' | 'setSaving'>> & {
      sessionId: string | null;
    },
): void {
  ctx.historyPushAccRef.current = [];
  ctx.preActionSnapshotRef.current = null;
  if (ctx.historyPushTimerRef.current) {
    clearTimeout(ctx.historyPushTimerRef.current);
    ctx.historyPushTimerRef.current = null;
  }
  if (ctx.sharedHistoryCacheRef) ctx.sharedHistoryCacheRef.current = { stack: [], pointer: -1 };
  ctx.setSharedHistoryState?.({ canUndo: false });
  ctx.setSaving?.(false);
  if (ctx.sessionId) clearSharedHistory(ctx.sessionId).catch(console.error);
}

// ── Restore ──────────────────────────────────────────────────

/** Everything a restore writes through. `sessionId` null restores locally only. */
export interface RestoreContext {
  sessionId: string | null;
  roster: Combatant[];
  undoingRef: MutableRefObject<boolean>;
  narrativeQueueRef?: MutableRefObject<unknown[]>;
  narrativeFlushTimerRef?: MutableRefObject<Timer | null>;
  restoreStates: (states: Record<string, CombatState>, narrativeCounts?: Record<string, number>) => void;
  setPendingAttack: (attack: null) => void;
  clearAllPendingDefences: (() => void) | null;
  clearPendingDefencesSince?: ((watermark: number | null | undefined) => void) | null;
  setTurnTrackerState?: Dispatch<SetStateAction<TurnTrackerState>>;
  syncTurnTrackerToFirestore?: (state: TurnTrackerState) => void;
  frame?: PhaseFrame | null;
  imposeFrame?: ((frame: PhaseFrame) => void) | null;
  /** The Cohesion pools as they stand: an undo that puts only those back still does something. */
  cohesion?: unknown;
  /** Put the Cohesion pools back (a compare-and-set; Squad Mode). */
  imposeCohesion?: ((cohesion: unknown) => void) | null;
  /** Put tokens back where the snapshot says they stood (an undone charge, 2026-10-21). */
  restoreTokens?: (tokens: Record<string, unknown>[]) => void;
  /** Delete one move line from the log, locally and on the server (an undone charge's own). */
  dropLine?: (line: ChargeLine) => void;
}

/** Clear the pending narrative queue, so stale entries are not flushed after the undo. */
function dropQueuedNarrative(ctx: RestoreContext): void {
  if (ctx.narrativeQueueRef) ctx.narrativeQueueRef.current.length = 0;
  if (ctx.narrativeFlushTimerRef?.current) {
    clearTimeout(ctx.narrativeFlushTimerRef.current);
    ctx.narrativeFlushTimerRef.current = null;
  }
}

/**
 * UNDOING AN ATTACK TAKES ITS REACTIONS WITH IT - but only its own. The
 * snapshot's watermark says which reactions the undone action created;
 * clearing every outstanding one would cancel a reaction owed to
 * somebody else's attack, which then expires into damage nobody chose.
 */
function cancelUndoneReactions(ctx: RestoreContext, snapshot: HistorySnapshot): void {
  if (ctx.clearPendingDefencesSince) ctx.clearPendingDefencesSince(snapshot.narrativeWatermark ?? null);
  else if (ctx.clearAllPendingDefences) ctx.clearAllPendingDefences();
}

/**
 * Write the restore to the server: the clocked states (combatants/* is
 * the only collection an undo touches since v7.6.0), then the shared
 * narrative after the watermark.
 */
function writeRestore(ctx: RestoreContext, snapshot: HistorySnapshot, restored: Combatant[]): void {
  const { sessionId } = ctx;
  if (!sessionId) return;
  const states = restored
    .filter((c) => snapshot.states[c.id])
    .map((c) => ({ id: c.id, state: c.state as unknown as Record<string, unknown> }));
  batchRestoreCombatantStates(sessionId, states).catch(console.error);
  if (snapshot.narrativeWatermark) deleteNarrativeAfter(sessionId, snapshot.narrativeWatermark).catch(console.error);
}

/** The tracker as the snapshot had it, set and sent whole - the one sanctioned replace. */
function restoreTracker(ctx: RestoreContext, snapshot: HistorySnapshot): void {
  if (!snapshot.turnTracker || !ctx.setTurnTrackerState) return;
  ctx.setTurnTrackerState(snapshot.turnTracker);
  if (ctx.syncTurnTrackerToFirestore) ctx.syncTurnTrackerToFirestore(snapshot.turnTracker);
}

/**
 * Put a shared snapshot back: locally, on the server, and on the tracker.
 *
 * `undoingRef` is held for the whole restore so the push effect does not
 * record the restore as an action, and released after the render settles.
 */
function restoreFromSharedSnapshot(ctx: RestoreContext, snapshotIn: HistorySnapshot): void {
  // Tolerate a snapshot written by an older client (no `states` yet).
  const snapshot: HistorySnapshot = { ...snapshotIn, states: snapshotIn.states ?? {} };
  ctx.undoingRef.current = true;
  dropQueuedNarrative(ctx);
  const restored = clockedRestore(ctx.roster, snapshot);
  if (!restoreChangesAnything(ctx.roster, restored, snapshot) && !cohesionDiffers(snapshot, ctx.cohesion)) {
    ctx.undoingRef.current = false;
    return;
  }
  ctx.restoreStates(clockedStates(restored, snapshot), snapshot.narrativeLogCounts);
  ctx.setPendingAttack(null);
  cancelUndoneReactions(ctx, snapshot);
  writeRestore(ctx, snapshot, restored);
  restoreTracker(ctx, snapshot);
  // The frame is the GM's to write: a player's undo leaves it where it is.
  if (snapshot.direction) ctx.imposeFrame?.(snapshot.direction);
  if (snapshot.cohesion !== undefined) ctx.imposeCohesion?.(snapshot.cohesion);
  requestAnimationFrame(() => {
    setTimeout(() => {
      ctx.undoingRef.current = false;
    }, 50);
  });
}

// ── Undo ─────────────────────────────────────────────────────

/**
 * Reconcile the optimistic step with the server's delta move - immune to a stale cache. A charge's own undo (its
 * charger back, its move line gone) waits for this: only when the server agrees the step left `leaving` - a stale
 * cache may think it was the charge when another seat's action is the one undone.
 */
async function confirmUndo(
  ctx: SharedHistoryContext & RestoreContext,
  optimisticPointer: number,
  leaving: HistorySnapshot | undefined,
): Promise<void> {
  const result = await moveSharedHistoryPointer(ctx.sessionId, -1);
  if (!result) return;
  if (result.pointer !== optimisticPointer) restoreFromSharedSnapshot(ctx, result.entry);
  else undoCharge(ctx, leaving);
  const cacheRef = ctx.sharedHistoryCacheRef;
  adoptPointer(ctx, cacheRef && { ...cacheRef.current, pointer: result.pointer }, result.pointer);
}

/** Undoing past a charge puts the charger back where the run began, and takes the run's own move line (2026-10-21). */
function undoCharge(ctx: RestoreContext, leaving: HistorySnapshot | undefined): void {
  if (leaving?.undoTokens?.length) ctx.restoreTokens?.(leaving.undoTokens);
  if (leaving?.chargeLine) ctx.dropLine?.(leaving.chargeLine);
}

/**
 * Undo one shared step: optimistically (cache, button, restore) and then
 * confirmed by the server. One undo/redo at a time - `undoRedoBusyRef`
 * owns the pointer while it runs.
 */
export async function undoShared(ctx: SharedHistoryContext & RestoreContext): Promise<void> {
  const { undoRedoBusyRef: busyRef, sharedHistoryCacheRef: cacheRef } = ctx;
  // The newest action not written yet: this Undo waits for it, or it would reach the entry before - and a Ctrl+Z
  // pressed then is never lost (2026-10-24).
  await untilSaved(ctx);
  if (busyRef?.current) return;
  const cache = cacheRef?.current;
  if (!cache || cache.pointer <= 0) return;
  const optimisticPointer = cache.pointer - 1;
  const optimisticEntry = cache.stack[optimisticPointer];
  if (!optimisticEntry) return;
  if (busyRef) busyRef.current = true;
  adoptPointer(ctx, { ...cache, pointer: optimisticPointer }, optimisticPointer);
  restoreFromSharedSnapshot(ctx, optimisticEntry);
  try {
    await confirmUndo(ctx, optimisticPointer, cache.stack[cache.pointer]);
  } catch (e) {
    console.error('Undo failed:', e);
  } finally {
    if (busyRef) busyRef.current = false;
  }
}
